import React, { useContext, useRef, useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion/dist/framer-motion'
import parse, { domToReact } from 'html-react-parser'

import { DataContext } from '../../../Context/DataContext'
import { UIContext } from '../../../Context/UIContext'

import usePreloadImage from '../../../usePreloadImage'
import useLockBodyScroll from '../../../useLockBodyScroll'
import MemberAward from './MemberAward'

const options = {
    replace: ({ attribs, children }) => {
        if (!attribs) return;

        const tagNames = ['h1', 'h2', 'h3', 'p', 'ul'];
        const tagName = attribs.tagName;

        if (tagNames.includes(tagName)) {
            if (tagName === 'ul') {
                return (
                    <ul>
                        {children.map((child, i) => 
                            <li key={`sub-item-${i}`}>
                                {domToReact(child.children, options)}
                            </li>
                        )}
                    </ul>
                );
            } else {
                return React.createElement(tagName, {}, domToReact(children, options));
            }
        }
    }
}

const MemberModal = React.forwardRef((props, ref) => {
    const { strings } = useContext(DataContext)
    const { setMemberModalRef, selectedMember } = useContext(UIContext)
    const modalRef = useRef(null)
    const bottomDiv = useRef(null)
    const bottomSvg = useRef(null)
    const [ marginBottom, setMarginBottom ] = useState(0)
    const [ paddingBottom, setPaddingBottom ] = useState(0)
    useLockBodyScroll(selectedMember !== null)

    const imageLoaded = usePreloadImage(selectedMember?.image)

    const handleUp = useCallback(() => {
        if (modalRef.current) {
            modalRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [modalRef])

    useEffect(() => {
        if (bottomSvg.current) {
            setPaddingBottom(bottomSvg.current.clientHeight)
        }
        if (bottomDiv.current) {
            setMarginBottom(bottomDiv.current.clientHeight)
        }
        if (modalRef.current) {
            setMemberModalRef(modalRef)
        }
    }, [selectedMember, setMemberModalRef])

    useEffect(() => {
        if (modalRef.current) {
            if (typeof window !== 'undefined') {
                window.scrollTo({ top: 0, behavior: 'instant' });
            }
            modalRef.current.scrollTo({ top: 0, behavior: 'instant' });
        }
    }, [modalRef])

    return (
        <AnimatePresence mode="wait">
            {selectedMember &&
                <motion.div
                    ref={ref}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, transition: {
                        duration: 0.4
                    } }}
                    transition={{ duration: 0.5, type: "ease" }}
                >
                    <motion.div
                        ref={modalRef}
                        className="mga-membermodal"
                    >
                        <motion.div
                            key="mga-membermodal__container"
                            className="mga-membermodal__container"
                            initial={{ opacity: 0, y: '100%' }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, type: "spring", stiffness: 50 }}
                        >
                            <div className="mga-membermodal__container__main" style={{ marginBottom: marginBottom - paddingBottom, paddingBottom }}>
                                <div className="mga-membermodal__top">
                                    <div className="mga-membermodal__wrapper">
                                        <div className="mga-membermodal__header">
                                            <h2 className="mga-membermodal__header__name">{ selectedMember.name }</h2>
                                            <figure
                                                className={`mga-membermodal__header__image${!imageLoaded ? ' mga-img-loading' : ''}`}
                                            >
                                                { imageLoaded && <img src={selectedMember.image} alt={selectedMember.name} /> }
                                            </figure>
                                        </div>
                                        <div className="mga-membermodal__main">
                                            {parse(selectedMember.info, options)}
                                            <a className="mga-membermodal__main__more" href={selectedMember.cv} target="_blank" rel="download noopener noreferrer">{strings.readmore} {selectedMember.name}</a>
                                        </div>
                                        {(selectedMember.awards.intro || selectedMember.awards.items) &&
                                            <div className="mga-membermodal__awards">
                                                <h3>{strings.awards}</h3>
                                                {selectedMember.awards.intro && 
                                                    parse(selectedMember.awards.intro, options)
                                                }
                                                {selectedMember.awards.items && selectedMember.awards.items.length > 0 &&
                                                    <motion.ul className="mga-memberawards">
                                                        {selectedMember.awards.items.map((item, index) => (
                                                            <MemberAward key={`award-${index}`} item={item} />
                                                        ))}
                                                    </motion.ul>
                                                }
                                            </div>
                                        }
                                    </div>
                                </div>
                                <svg ref={bottomSvg} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1365 39">
                                    <path d="M0 39.302V.002h1366v3.62c-128.822.22-260.092 1.367-390.164 3.41a28499.216 28499.216 0 0 0-398.219 9.05 28027.158 28027.158 0 0 0-292.758 10.234A26601.522 26601.522 0 0 0 0 39.302z"/>
                                </svg>
                            </div>
                        </motion.div>
                        <motion.div
                            ref={bottomDiv}
                            key="mga-membermodal__bottom"
                            className="mga-membermodal__bottom"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.1, delay: 0.5 }}
                        >
                            <div className="mga-membermodal__wrapper">
                                <div className="mga-membermodal__bottom__header">
                                    <motion.h3>{ strings.contact }</motion.h3>
                                    <button
                                        className="mga-membermodal__bottom__up"
                                        onClick={handleUp}
                                    >
                                        { strings.up }
                                    </button>
                                </div>
                                <ul className="mga-membermodal__contact">
                                    {selectedMember.address && 
                                        <li className="mga-membermodal__contact__item mga-membermodal__contact__item--address">
                                            <span>{ selectedMember.address }</span>
                                        </li>
                                    }
                                    {selectedMember.email && 
                                        <li className="mga-membermodal__contact__item mga-membermodal__contact__item--email">
                                            <a href={`mailto:${selectedMember.email}`} target="_blank" rel="nofollow noopener noreferrer">{ selectedMember.email }</a>
                                        </li>
                                    }
                                    {(selectedMember.twitter && selectedMember.twitter.url) && 
                                        <li className="mga-membermodal__contact__item mga-membermodal__contact__item--twitter">
                                            <a href={ selectedMember.twitter.url } target="_blank" rel="nofollow noopener noreferrer">{ selectedMember.twitter.user }</a>
                                        </li>
                                    }
                                    {(selectedMember.instagram && selectedMember.instagram.url) && 
                                        <li className="mga-membermodal__contact__item mga-membermodal__contact__item--instagram">
                                            <a href={ selectedMember.instagram.url } target="_blank" rel="nofollow noopener noreferrer">{ selectedMember.instagram.user }</a>
                                        </li>
                                    }
                                    {(selectedMember.linkedin && selectedMember.linkedin.url) && 
                                        <li className="mga-membermodal__contact__item mga-membermodal__contact__item--linkedin">
                                            <a href={ selectedMember.linkedin.url } target="_blank" rel="nofollow noopener noreferrer">{ selectedMember.linkedin.user }</a>
                                        </li>
                                    }
                                </ul>
                                {selectedMember.cv &&
                                    <a className="mga-membermodal__contact__more" href={selectedMember.cv} target="_blank" rel="download noopener noreferrer">{strings.readmore} {selectedMember.name}</a>
                                }
                            </div>
                        </motion.div>
                    </motion.div>
                </motion.div>
            }
        </AnimatePresence>
    )
})

const MotionMemberModal = motion(MemberModal)

export default React.memo(MotionMemberModal)